// TODO There's a problem with infering option type in SelectAsync component implementation, probably due to ref type declaration
import {useCallback, useEffect, useId, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";

import {WideContent} from "@pg-design/grid-module";
import {borderRadius, calculateRemSize, flex, flexAbsoluteCenter, flexDirection, mt, p, pt, zIndex} from "@pg-design/helpers-css";
import {SearchBoldIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {SystemModal} from "@pg-design/modal";
import {Components, ControlProps, MenuProps, OptionProps, SelectAsync, SelectInstance} from "@pg-design/select-module";
import {Text} from "@pg-design/text-module";
import {debounce, uniq} from "@pg-mono/nodash";
import {parseSearch} from "@pg-mono/request";
import {rpAppPath} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {DesktopBlend} from "../../search/components/atoms/atoms";
import {fetchArticlesResults} from "../api/fetch_articles_results";

type ResultOption = {categoryId?: number; articlesCount: number; slug: string; label: string; type: string; value: string};
type ResultOptions = ResultOption[];
type GetArticlesOptionsDebounced = (inputValue: string, callback: (options: ResultOptions) => void) => void;
type LoadOptions = (inputValue: string, callback: (options: ResultOptions) => void) => Promise<unknown[]>;
export const ArticleSearchBar = () => {
    const articleCategories = useSelector((state: IRPStore) => state.siteMap.articleCategories);
    const {isMobile} = useUserDevice();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [cachedOptions, setCachedOptions] = useState<ResultOptions>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [value, setValue] = useState<null | {value: string; label: string}>(null);
    const selectRef = useRef<SelectInstance<unknown>>(null);
    const mobileSelectRef = useRef<SelectInstance<unknown>>(null);
    const location = useLocation();
    const phrase = parseSearch(location.search)?.phrase;
    const instanceId = useId();

    const getArticleOptions = async (eventValue: string) => {
        const value = !!eventValue ? eventValue : inputValue;
        const articlesArr = await fetchArticlesResults(value);
        if (!value) {
            return [] as ResultOptions;
        }
        const articleCategoryPair = articlesArr?.results?.flatMap(({categories, id}) => categories.map((categoryId) => ({categoryId, articleId: id})));

        const categoriesIds = uniq(articleCategoryPair.map(({categoryId}) => categoryId));
        const resultOptions: ResultOptions = categoriesIds.map((categoryId) => {
            const foundCategory = articleCategories.find(({id}) => id === categoryId);
            const label = foundCategory?.name as string;
            const slug = `${rpAppPath.article.list}kategorie/${foundCategory?.slug}?phrase=${value}`;
            const articlesCount = articleCategoryPair.filter((pair) => pair.categoryId === categoryId).length;
            return {categoryId, articlesCount, slug, label, type: "Kategoria", value: slug};
        });

        if (value && !!articlesArr.count) {
            resultOptions.unshift({
                label: value,
                articlesCount: articlesArr.count,
                slug: `${rpAppPath.article.list}?phrase=${value}`,
                value: `${rpAppPath.article.list}?phrase=${value}`,
                type: "Artykuły"
            });
        }

        const finalOptions = resultOptions.filter(({slug, label}) => !!slug && !!label).sort((a, b) => b.articlesCount - a.articlesCount);
        setCachedOptions(finalOptions);
        return finalOptions;
    };

    const getArticlesOptionsDebounced: GetArticlesOptionsDebounced = useCallback(
        debounce((inputValue, callback) => {
            //TODO: right now we need to cast each parameter of the callback, we should modify debounce's type so that it picks up types in order of passed params
            getArticleOptions(inputValue as string).then((options) => (callback as (options: ResultOptions) => void)(options));
        }, 500),
        []
    );

    useEffect(() => {
        if (!isMobile) {
            return;
        }
        if (!isMenuOpen) {
            setTimeout(() => selectRef.current?.blur(), 250);
            return;
        }
        setTimeout(() => mobileSelectRef.current?.focus(), 1);
    }, [isMenuOpen]);

    useEffect(() => {
        if (phrase) {
            setInputValue(phrase as string);
            getArticleOptions(phrase as string);
        }
    }, []);

    return (
        <>
            <div css={bg} />
            <WideContent css={wrapper}>
                <SelectAsync
                    instanceId={instanceId}
                    ref={selectRef}
                    css={selectStyle}
                    components={{Option, Menu, DropdownIndicator: null, Control, NoOptionsMessage}}
                    disableDropdownIndicator
                    onMenuClose={() => !isMobile && setIsMenuOpen(false)}
                    onMenuOpen={() => setIsMenuOpen(true)}
                    menuIsOpen={isMenuOpen && !!inputValue}
                    placeholder={(phrase as string) || "Jaki temat Cię interesuje?"}
                    onInputChange={(value, meta) => {
                        if (meta.action === "input-change") {
                            setInputValue(value);
                        }
                    }}
                    inputValue={inputValue}
                    loadOptions={getArticlesOptionsDebounced as LoadOptions}
                    defaultOptions={cachedOptions}
                    onChange={() => {
                        setInputValue("");
                        setIsLoading(true);
                        setValue({label: inputValue, value: inputValue});
                    }}
                    value={value}
                    loadingMessage={() => <>Ładowanie</>}
                />
            </WideContent>
            {(isMenuOpen || isLoading) && !isMobile && (
                <DesktopBlend top="11rem" isActive css={flexAbsoluteCenter}>
                    {isLoading && (
                        <div css={[flexAbsoluteCenter, pt(8)]}>
                            <Loader padding="xl" />
                        </div>
                    )}
                </DesktopBlend>
            )}
            <SystemModal
                isOpen={isMenuOpen && !!isMobile}
                onModalClose={() => {
                    setIsMenuOpen(false);
                    selectRef.current?.blur();
                }}
                variant="fit"
                headerVariant="gray"
                css={modalContentStyle}
            >
                <SystemModal.Header>
                    <Text variant="body_copy_2">Szukaj</Text>
                </SystemModal.Header>
                <SystemModal.Content>
                    {!isLoading && (
                        <SelectAsync
                            instanceId={instanceId}
                            defaultOptions={cachedOptions}
                            ref={mobileSelectRef}
                            css={selectStyle}
                            components={{
                                Option,
                                DropdownIndicator: null,
                                Menu,
                                Control,
                                NoOptionsMessage,
                                MenuList: (props) => <Components.MenuList {...props} css={menuListStyle} />
                            }}
                            disableDropdownIndicator
                            onMenuClose={() => !isMobile && setIsMenuOpen(false)}
                            onMenuOpen={() => setIsMenuOpen(true)}
                            menuIsOpen={!!inputValue}
                            placeholder="Jaki temat Cię interesuje?"
                            onInputChange={(value, meta) => {
                                if (meta.action === "input-change") {
                                    setInputValue(value);
                                }
                            }}
                            inputValue={inputValue}
                            loadOptions={getArticlesOptionsDebounced as LoadOptions}
                            onChange={() => {
                                setIsLoading(true);
                            }}
                            loadingMessage={() => <div css={flexAbsoluteCenter}>Ładowanie</div>}
                        />
                    )}
                    {isLoading && (
                        <div css={[flexAbsoluteCenter, pt(4)]}>
                            <Loader />
                        </div>
                    )}
                </SystemModal.Content>
            </SystemModal>
        </>
    );
};

const Option = (props: OptionProps<unknown>) => {
    const theme = useTheme();
    const optionProps = props as OptionProps<ResultOption>;
    return (
        <Components.Option {...props} css={separatorStyle}>
            <a href={optionProps.data.slug} css={optionStyle}>
                <div css={labelContainer}>
                    <Text variant="body_copy_1">{props.label}</Text>
                    <Text variant="info_txt_1" color={theme.colors.gray[700]}>
                        {optionProps.data.type}
                    </Text>
                </div>
                {!!optionProps.data.articlesCount && optionProps.data.type !== "Kategoria" && (
                    <Text variant="info_txt_1" color={theme.colors.gray[600]}>
                        {optionProps.data.articlesCount} artykuły
                    </Text>
                )}
            </a>
        </Components.Option>
    );
};

const Menu = (props: MenuProps) => (
    <Components.Menu {...props} css={menuStyle}>
        {props.selectProps.isLoading ? <div css={flexAbsoluteCenter}>Ładowanie</div> : props.children}
    </Components.Menu>
);

const Control = (props: ControlProps) => {
    const theme = useTheme();
    return (
        <Components.Control {...props} css={controlStyle}>
            <SearchBoldIcon wrapperSize="6.4" wrapperColor={theme.colors.primary} size="2.4" css={searchIconStyle} wrapperType="square" />
            {props.children}
        </Components.Control>
    );
};

const NoOptionsMessage = () => {
    return <div css={flexAbsoluteCenter}>Brak wyników dla szukanej frazy</div>;
};

const controlStyle = css`
    border-radius: ${calculateRemSize(1)} ${calculateRemSize(2)} ${calculateRemSize(2)} ${calculateRemSize(1)};
    border: none;
    overflow: hidden;
    cursor: text;
`;

const optionStyle = css`
    ${flex("center", "space-between")};
    :hover,
    :focus {
        color: initial;
    }
`;
const menuStyle = (theme: Theme) => css`
    ${borderRadius(2)};
    border: none;
    ${p(2)};
    ${mt(2)};

    @media (max-width: ${theme.breakpoints.md}) {
        box-shadow: none;
        ${p(0)};
    }
`;

const wrapper = css`
    position: relative;
    height: 110px;
    ${flex("center")};
`;

const bg = css`
    width: 100vw;
    max-width: 100%;
    background-color: #e1d7cd;
    height: 110px;
    position: absolute;
    left: 50%;
    top: 60px;
    translate: -50% 0;
    z-index: -1;
`;

const labelContainer = css`
    ${flex()};
    ${flexDirection("column")};
    gap: ${calculateRemSize(1)};
`;

const selectStyle = (theme: Theme) => css`
    ${zIndex(1001)};
    width: 100%;
    max-width: 550px;

    @media (max-width: ${theme.breakpoints.md}) {
        max-width: initial;
    }
`;

const separatorStyle = (theme: Theme) => css`
    :not(:last-of-type) {
        border-bottom: 1px solid ${theme.colors.gray[200]};
    }
`;

const searchIconStyle = css`
    cursor: default;
`;

const modalContentStyle = (theme: Theme) => css`
    @media (max-width: ${theme.breakpoints.md}) {
        inset: 0;
    }
`;

const menuListStyle = css`
    max-height: initial;
`;
// TODO There's a problem with infering option type in SelectAsync component implementation, probably due to ref type declaration
import {useCallback, useEffect, useId, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css, Theme, useTheme} from "@emotion/react";

import {WideContent} from "@pg-design/grid-module";
import {borderRadius, calculateRemSize, flex, flexAbsoluteCenter, flexDirection, mt, p, pt, zIndex} from "@pg-design/helpers-css";
import {SearchBoldIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {SystemModal} from "@pg-design/modal";
import {Components, ControlProps, MenuProps, OptionProps, SelectAsync, SelectInstance} from "@pg-design/select-module";
import {Text} from "@pg-design/text-module";
import {debounce, uniq} from "@pg-mono/nodash";
import {parseSearch} from "@pg-mono/request";
import {rpAppPath} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {DesktopBlend} from "../../search/components/atoms/atoms";
import {fetchArticlesResults} from "../api/fetch_articles_results";

type ResultOption = {categoryId?: number; articlesCount: number; slug: string; label: string; type: string; value: string};
type ResultOptions = ResultOption[];
type GetArticlesOptionsDebounced = (inputValue: string, callback: (options: ResultOptions) => void) => void;
type LoadOptions = (inputValue: string, callback: (options: ResultOptions) => void) => Promise<unknown[]>;
export const ArticleSearchBar = () => {
    const articleCategories = useSelector((state: IRPStore) => state.siteMap.articleCategories);
    const {isMobile} = useUserDevice();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [cachedOptions, setCachedOptions] = useState<ResultOptions>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [value, setValue] = useState<null | {value: string; label: string}>(null);
    const selectRef = useRef<SelectInstance<unknown>>(null);
    const mobileSelectRef = useRef<SelectInstance<unknown>>(null);
    const location = useLocation();
    const phrase = parseSearch(location.search)?.phrase;
    const instanceId = useId();

    const getArticleOptions = async (eventValue: string) => {
        const value = !!eventValue ? eventValue : inputValue;
        const articlesArr = await fetchArticlesResults(value);
        if (!value) {
            return [] as ResultOptions;
        }
        const articleCategoryPair = articlesArr?.results?.flatMap(({categories, id}) => categories.map((categoryId) => ({categoryId, articleId: id})));

        const categoriesIds = uniq(articleCategoryPair.map(({categoryId}) => categoryId));
        const resultOptions: ResultOptions = categoriesIds.map((categoryId) => {
            const foundCategory = articleCategories.find(({id}) => id === categoryId);
            const label = foundCategory?.name as string;
            const slug = `${rpAppPath.article.list}kategorie/${foundCategory?.slug}?phrase=${value}`;
            const articlesCount = articleCategoryPair.filter((pair) => pair.categoryId === categoryId).length;
            return {categoryId, articlesCount, slug, label, type: "Kategoria", value: slug};
        });

        if (value && !!articlesArr.count) {
            resultOptions.unshift({
                label: value,
                articlesCount: articlesArr.count,
                slug: `${rpAppPath.article.list}?phrase=${value}`,
                value: `${rpAppPath.article.list}?phrase=${value}`,
                type: "Artykuły"
            });
        }

        const finalOptions = resultOptions.filter(({slug, label}) => !!slug && !!label).sort((a, b) => b.articlesCount - a.articlesCount);
        setCachedOptions(finalOptions);
        return finalOptions;
    };

    const getArticlesOptionsDebounced: GetArticlesOptionsDebounced = useCallback(
        debounce((inputValue, callback) => {
            //TODO: right now we need to cast each parameter of the callback, we should modify debounce's type so that it picks up types in order of passed params
            getArticleOptions(inputValue as string).then((options) => (callback as (options: ResultOptions) => void)(options));
        }, 500),
        []
    );

    useEffect(() => {
        if (!isMobile) {
            return;
        }
        if (!isMenuOpen) {
            setTimeout(() => selectRef.current?.blur(), 250);
            return;
        }
        setTimeout(() => mobileSelectRef.current?.focus(), 1);
    }, [isMenuOpen]);

    useEffect(() => {
        if (phrase) {
            setInputValue(phrase as string);
            getArticleOptions(phrase as string);
        }
    }, []);

    return (
        <>
            <div css={bg} />
            <WideContent css={wrapper}>
                <SelectAsync
                    instanceId={instanceId}
                    ref={selectRef}
                    css={selectStyle}
                    components={{Option, Menu, DropdownIndicator: null, Control, NoOptionsMessage}}
                    disableDropdownIndicator
                    onMenuClose={() => !isMobile && setIsMenuOpen(false)}
                    onMenuOpen={() => setIsMenuOpen(true)}
                    menuIsOpen={isMenuOpen && !!inputValue}
                    placeholder={(phrase as string) || "Jaki temat Cię interesuje?"}
                    onInputChange={(value, meta) => {
                        if (meta.action === "input-change") {
                            setInputValue(value);
                        }
                    }}
                    inputValue={inputValue}
                    loadOptions={getArticlesOptionsDebounced as LoadOptions}
                    defaultOptions={cachedOptions}
                    onChange={() => {
                        setInputValue("");
                        setIsLoading(true);
                        setValue({label: inputValue, value: inputValue});
                    }}
                    value={value}
                    loadingMessage={() => <>Ładowanie</>}
                />
            </WideContent>
            {(isMenuOpen || isLoading) && !isMobile && (
                <DesktopBlend top="11rem" isActive css={flexAbsoluteCenter}>
                    {isLoading && (
                        <div css={[flexAbsoluteCenter, pt(8)]}>
                            <Loader padding="xl" />
                        </div>
                    )}
                </DesktopBlend>
            )}
            <SystemModal
                isOpen={isMenuOpen && !!isMobile}
                onModalClose={() => {
                    setIsMenuOpen(false);
                    selectRef.current?.blur();
                }}
                variant="fit"
                headerVariant="gray"
                css={modalContentStyle}
            >
                <SystemModal.Header>
                    <Text variant="body_copy_2">Szukaj</Text>
                </SystemModal.Header>
                <SystemModal.Content>
                    {!isLoading && (
                        <SelectAsync
                            instanceId={instanceId}
                            defaultOptions={cachedOptions}
                            ref={mobileSelectRef}
                            css={selectStyle}
                            components={{
                                Option,
                                DropdownIndicator: null,
                                Menu,
                                Control,
                                NoOptionsMessage,
                                MenuList: (props) => <Components.MenuList {...props} css={menuListStyle} />
                            }}
                            disableDropdownIndicator
                            onMenuClose={() => !isMobile && setIsMenuOpen(false)}
                            onMenuOpen={() => setIsMenuOpen(true)}
                            menuIsOpen={!!inputValue}
                            placeholder="Jaki temat Cię interesuje?"
                            onInputChange={(value, meta) => {
                                if (meta.action === "input-change") {
                                    setInputValue(value);
                                }
                            }}
                            inputValue={inputValue}
                            loadOptions={getArticlesOptionsDebounced as LoadOptions}
                            onChange={() => {
                                setIsLoading(true);
                            }}
                            loadingMessage={() => <div css={flexAbsoluteCenter}>Ładowanie</div>}
                        />
                    )}
                    {isLoading && (
                        <div css={[flexAbsoluteCenter, pt(4)]}>
                            <Loader />
                        </div>
                    )}
                </SystemModal.Content>
            </SystemModal>
        </>
    );
};

const Option = (props: OptionProps<unknown>) => {
    const theme = useTheme();
    const optionProps = props as OptionProps<ResultOption>;
    return (
        <Components.Option {...props} css={separatorStyle}>
            <a href={optionProps.data.slug} css={optionStyle}>
                <div css={labelContainer}>
                    <Text variant="body_copy_1">{props.label}</Text>
                    <Text variant="info_txt_1" color={theme.colors.gray[700]}>
                        {optionProps.data.type}
                    </Text>
                </div>
                {!!optionProps.data.articlesCount && optionProps.data.type !== "Kategoria" && (
                    <Text variant="info_txt_1" color={theme.colors.gray[600]}>
                        {optionProps.data.articlesCount} artykuły
                    </Text>
                )}
            </a>
        </Components.Option>
    );
};

const Menu = (props: MenuProps) => (
    <Components.Menu {...props} css={menuStyle}>
        {props.selectProps.isLoading ? <div css={flexAbsoluteCenter}>Ładowanie</div> : props.children}
    </Components.Menu>
);

const Control = (props: ControlProps) => {
    const theme = useTheme();
    return (
        <Components.Control {...props} css={controlStyle}>
            <SearchBoldIcon wrapperSize="6.4" wrapperColor={theme.colors.primary} size="2.4" css={searchIconStyle} wrapperType="square" />
            {props.children}
        </Components.Control>
    );
};

const NoOptionsMessage = () => {
    return <div css={flexAbsoluteCenter}>Brak wyników dla szukanej frazy</div>;
};

const controlStyle = css`
    border-radius: ${calculateRemSize(1)} ${calculateRemSize(2)} ${calculateRemSize(2)} ${calculateRemSize(1)};
    border: none;
    overflow: hidden;
    cursor: text;
`;

const optionStyle = css`
    ${flex("center", "space-between")};
    :hover,
    :focus {
        color: initial;
    }
`;
const menuStyle = (theme: Theme) => css`
    ${borderRadius(2)};
    border: none;
    ${p(2)};
    ${mt(2)};

    @media (max-width: ${theme.breakpoints.md}) {
        box-shadow: none;
        ${p(0)};
    }
`;

const wrapper = css`
    position: relative;
    height: 110px;
    ${flex("center")};
`;

const bg = css`
    width: 100vw;
    max-width: 100%;
    background-color: #e1d7cd;
    height: 110px;
    position: absolute;
    left: 50%;
    top: 60px;
    translate: -50% 0;
    z-index: -1;
`;

const labelContainer = css`
    ${flex()};
    ${flexDirection("column")};
    gap: ${calculateRemSize(1)};
`;

const selectStyle = (theme: Theme) => css`
    ${zIndex(1001)};
    width: 100%;
    max-width: 550px;

    @media (max-width: ${theme.breakpoints.md}) {
        max-width: initial;
    }
`;

const separatorStyle = (theme: Theme) => css`
    :not(:last-of-type) {
        border-bottom: 1px solid ${theme.colors.gray[200]};
    }
`;

const searchIconStyle = css`
    cursor: default;
`;

const modalContentStyle = (theme: Theme) => css`
    @media (max-width: ${theme.breakpoints.md}) {
        inset: 0;
    }
`;

const menuListStyle = css`
    max-height: initial;
`;
